import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Loader2,
  Save,
  Edit3,
  X,
  FileText,
  User,
  MapPin,
  Briefcase,
  DollarSign,
  Wallet,
  CreditCard,
  CheckCircle,
  AlertCircle,
  Plus
} from 'lucide-react';
import { fetchWithAuth, BASE_URL } from '../../api/client';
import LoanApplicationForm from '../../../borrower/components/LoanApplicationForm';

// Reuse the same styling constants as borrower form
const baseInputStyle = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: '10px',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'rgba(148, 163, 184, 0.3)',
  background: 'rgba(255, 255, 255, 0.05)',
  color: '#e2e8f0',
  fontSize: '14px',
  outline: 'none',
  transition: 'all 0.2s ease',
  boxSizing: 'border-box',
};

const focusStyle = {
  borderColor: 'rgba(56, 189, 248, 0.6)',
  boxShadow: '0 0 0 3px rgba(56, 189, 248, 0.15)',
};

const labelStyle = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '12px',
  fontWeight: 600,
  color: '#94a3b8',
};

const sectionHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginBottom: '16px',
  paddingBottom: '12px',
  borderBottom: '1px solid rgba(148, 163, 184, 0.15)',
};

// Format helpers
const formatCurrency = (value) => {
  const num = parseFloat(value) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(num);
};

const formatPhone = (val) => {
  const digits = (val || '').replace(/\D/g, '');
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const formatSSN = (val) => {
  if (!val || val.length < 4) return '***-**-****';
  return `***-**-${val.slice(-4)}`;
};

// Editable Field Component
function EditableField({ label, value, field, isEditing, onChange, type = 'text', options = null }) {
  const [focused, setFocused] = useState(false);

  if (!isEditing) {
    return (
      <div style={{ marginBottom: '12px' }}>
        <label style={labelStyle}>{label}</label>
        <div style={{ color: '#e2e8f0', fontSize: '14px' }}>
          {value || '—'}
        </div>
      </div>
    );
  }

  if (options) {
    return (
      <div style={{ marginBottom: '12px' }}>
        <label style={labelStyle}>{label}</label>
        <select
          value={value || ''}
          onChange={(e) => onChange(field, e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            ...baseInputStyle,
            ...(focused ? focusStyle : {}),
          }}
        >
          <option value="">Select...</option>
          {options.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '12px' }}>
      <label style={labelStyle}>{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(field, e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          ...baseInputStyle,
          ...(focused ? focusStyle : {}),
        }}
      />
    </div>
  );
}

EditableField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  field: PropTypes.string.isRequired,
  isEditing: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  type: PropTypes.string,
  options: PropTypes.array,
};

// Section Component (mirrors borrower's SummarySection)
function Section({ title, icon, children, isEditing, onEdit }) {
  return (
    <div
      style={{
        marginBottom: '20px',
        padding: '20px',
        borderRadius: '12px',
        background: 'rgba(255, 255, 255, 0.03)',
        border: '1px solid rgba(148, 163, 184, 0.2)',
      }}
    >
      <div style={sectionHeaderStyle}>
        <span style={{ color: '#38bdf8' }}>{icon}</span>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#e2e8f0', flex: 1 }}>
          {title}
        </h3>
        {onEdit && !isEditing && (
          <button
            type="button"
            onClick={onEdit}
            style={{
              background: 'none',
              border: 'none',
              color: '#38bdf8',
              cursor: 'pointer',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <Edit3 size={14} /> Edit
          </button>
        )}
      </div>
      <div style={{ color: '#94a3b8', fontSize: '14px' }}>
        {children}
      </div>
    </div>
  );
}

Section.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  isEditing: PropTypes.bool,
  onEdit: PropTypes.func,
};

// Option definitions (same as borrower form)
const loanPurposeOptions = [
  { value: 'purchase', label: 'Purchase' },
  { value: 'refinance', label: 'Refinance' },
  { value: 'cashout', label: 'Cash-Out Refinance' },
];

const propertyTypeOptions = [
  { value: 'single-family', label: 'Single Family' },
  { value: 'condo', label: 'Condo' },
  { value: 'townhouse', label: 'Townhouse' },
  { value: 'multi-family', label: 'Multi-Family' },
];

const propertyUseOptions = [
  { value: 'primary', label: 'Primary Residence' },
  { value: 'secondary', label: 'Second Home' },
  { value: 'investment', label: 'Investment Property' },
];

const citizenshipOptions = [
  { value: 'us_citizen', label: 'U.S. Citizen' },
  { value: 'permanent_resident', label: 'Permanent Resident Alien' },
  { value: 'non_permanent_resident', label: 'Non-Permanent Resident Alien' },
];

const maritalStatusOptions = [
  { value: 'married', label: 'Married' },
  { value: 'separated', label: 'Separated' },
  { value: 'unmarried', label: 'Unmarried' },
];

const employmentStatusOptions = [
  { value: 'employed', label: 'Employed' },
  { value: 'self-employed', label: 'Self-Employed' },
  { value: 'retired', label: 'Retired' },
  { value: 'other', label: 'Other' },
];

const stateOptions = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' }, { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' }, { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' }, { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' }, { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' }, { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' }, { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' }, { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' }, { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
];

// Main Component
export default function LoanApplicationTab({ caseId, caseInfo, onUpdate }) {
  const [loanApp, setLoanApp] = useState(null);
  const [editedData, setEditedData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Fetch loan application
  const fetchLoanApplication = useCallback(async () => {
    if (!caseId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetchWithAuth(`${BASE_URL}/api/loan-applications/case/${caseId}`);

      if (response.status === 404) {
        setLoanApp(null);
        setIsLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch loan application');
      }

      const data = await response.json();

      // Handle data: null response (no loan application exists)
      if (data.data === null || (!data.data && !data.id)) {
        setLoanApp(null);
        setEditedData({});
        return;
      }

      setLoanApp(data.data || data);
      setEditedData(data.data?.application || data.application || {});
    } catch (err) {
      console.error('Error fetching loan application:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    fetchLoanApplication();
  }, [fetchLoanApplication]);

  // Handle field change
  const handleFieldChange = (section, field, value) => {
    setEditedData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  // Handle save
  const handleSave = async () => {
    if (!loanApp?.id) return;

    setIsSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const response = await fetchWithAuth(`${BASE_URL}/api/loan-applications/${loanApp.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ application: editedData }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Failed to save changes');
      }

      const result = await response.json();
      setLoanApp(result.data || result);
      setSaveSuccess(true);
      setIsEditing(false);
      onUpdate?.();

      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving loan application:', err);
      setSaveError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel editing
  const handleCancel = () => {
    setEditedData(loanApp?.application || {});
    setIsEditing(false);
    setSaveError(null);
  };

  // Handle form completion (after creating new application)
  const handleFormComplete = useCallback((result) => {
    setIsCreating(false);
    // Reload the application data
    fetchLoanApplication();
    onUpdate?.();
  }, [fetchLoanApplication, onUpdate]);

  // Handle form error
  const handleFormError = useCallback((errorMsg) => {
    setSaveError(errorMsg);
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px' }}>
        <Loader2 className="w-6 h-6 text-sky-400 animate-spin" />
        <span style={{ marginLeft: '12px', color: '#94a3b8' }}>Loading loan application...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{
        padding: '20px',
        borderRadius: '12px',
        background: 'rgba(239, 68, 68, 0.1)',
        border: '1px solid rgba(239, 68, 68, 0.3)',
        color: '#fca5a5',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <AlertCircle size={20} />
        <span>Error loading loan application: {error}</span>
      </div>
    );
  }

  // No application found - show create option
  if (!loanApp) {
    // Show the create form if user clicked "Create Application"
    if (isCreating) {
      return (
        <div>
          {/* Header with Cancel button */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#e2e8f0' }}>
                Create Loan Application
              </h2>
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#64748b' }}>
                Fill out the URLA form on behalf of the borrower
              </p>
            </div>
            <button
              onClick={() => setIsCreating(false)}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid rgba(148, 163, 184, 0.3)',
                background: 'transparent',
                color: '#94a3b8',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <X size={14} /> Cancel
            </button>
          </div>

          {saveError && (
            <div style={{
              padding: '12px 16px',
              borderRadius: '8px',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              color: '#fca5a5',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <AlertCircle size={16} />
              {saveError}
            </div>
          )}

          {/* Render the borrower's loan application form */}
          <LoanApplicationForm
            caseInfo={caseInfo || { id: caseId, caseId }}
            onComplete={handleFormComplete}
            onError={handleFormError}
          />
        </div>
      );
    }

    // Show prompt to create application
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center',
        color: '#94a3b8'
      }}>
        <FileText size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
        <h3 style={{ margin: '0 0 8px', color: '#e2e8f0', fontSize: '18px' }}>
          No Loan Application Found
        </h3>
        <p style={{ margin: '0 0 24px', fontSize: '14px' }}>
          No loan application has been submitted for this case yet.
        </p>
        <button
          onClick={() => setIsCreating(true)}
          style={{
            padding: '12px 24px',
            borderRadius: '10px',
            border: 'none',
            background: 'linear-gradient(135deg, #0ea5e9, #6366f1)',
            color: '#fff',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 20px rgba(14, 165, 233, 0.3)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          <Plus size={18} /> Create Loan Application
        </button>
      </div>
    );
  }

  const app = editedData || loanApp.application || {};
  const gettingStarted = app.gettingStarted || {};
  const personalInfo = app.personalInfo || {};
  const contactInfo = app.contactInfo || {};
  const employment = app.employment || {};
  const income = app.income || {};
  const assets = app.assets || {};
  const liabilities = app.liabilities || {};

  // Calculate totals
  const totalIncome = (income.incomes || []).reduce(
    (sum, inc) => sum + (parseFloat(inc.amount) || 0), 0
  );
  const totalAssets = (assets.assets || []).reduce(
    (sum, asset) => sum + (parseFloat(asset.balance) || 0), 0
  );
  const totalLiabilities = (liabilities.liabilities || []).reduce(
    (sum, l) => sum + (parseFloat(l.balance) || 0), 0
  );
  const totalMonthlyDebt = (liabilities.liabilities || [])
    .filter(l => !l.willPayOff)
    .reduce((sum, l) => sum + (parseFloat(l.monthlyPayment) || 0), 0);

  return (
    <div>
      {/* Header with Edit/Save buttons */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#e2e8f0' }}>
            Loan Application (URLA)
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#64748b' }}>
            Submitted: {loanApp.submittedAt ? new Date(loanApp.submittedAt).toLocaleString() : 'N/A'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {isEditing ? (
            <>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: '1px solid rgba(148, 163, 184, 0.3)',
                  background: 'transparent',
                  color: '#94a3b8',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <X size={14} /> Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #0ea5e9, #6366f1)',
                  color: '#fff',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: isSaving ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  opacity: isSaving ? 0.7 : 1,
                }}
              >
                {isSaving ? (
                  <><Loader2 size={14} className="animate-spin" /> Saving...</>
                ) : (
                  <><Save size={14} /> Save Changes</>
                )}
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              style={{
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid rgba(56, 189, 248, 0.4)',
                background: 'rgba(56, 189, 248, 0.1)',
                color: '#38bdf8',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <Edit3 size={14} /> Edit Application
            </button>
          )}
        </div>
      </div>

      {/* Success/Error Messages */}
      {saveSuccess && (
        <div style={{
          padding: '12px 16px',
          borderRadius: '8px',
          background: 'rgba(34, 197, 94, 0.1)',
          border: '1px solid rgba(34, 197, 94, 0.3)',
          color: '#4ade80',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <CheckCircle size={16} />
          Changes saved successfully!
        </div>
      )}
      {saveError && (
        <div style={{
          padding: '12px 16px',
          borderRadius: '8px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          color: '#fca5a5',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <AlertCircle size={16} />
          {saveError}
        </div>
      )}

      {/* Financial Summary Card (same as borrower) */}
      <div
        style={{
          marginBottom: '24px',
          padding: '24px',
          borderRadius: '16px',
          background: 'linear-gradient(135deg, rgba(56, 189, 248, 0.1), rgba(99, 102, 241, 0.05))',
          border: '1px solid rgba(56, 189, 248, 0.2)',
        }}
      >
        <h3 style={{ margin: '0 0 20px', fontSize: '18px', fontWeight: 600, color: '#e2e8f0', textAlign: 'center' }}>
          Financial Summary
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', textAlign: 'center' }}>
          <div>
            <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#94a3b8' }}>Monthly Income</p>
            <p style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#22c55e' }}>
              {formatCurrency(totalIncome)}
            </p>
          </div>
          <div>
            <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#94a3b8' }}>Total Assets</p>
            <p style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#38bdf8' }}>
              {formatCurrency(totalAssets)}
            </p>
          </div>
          <div>
            <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#94a3b8' }}>Total Liabilities</p>
            <p style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#f87171' }}>
              {formatCurrency(totalLiabilities)}
            </p>
          </div>
          <div>
            <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#94a3b8' }}>Monthly Debt</p>
            <p style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#fb923c' }}>
              {formatCurrency(totalMonthlyDebt)}
            </p>
          </div>
        </div>
      </div>

      {/* Loan Information Section */}
      <Section title="Loan Information" icon={<FileText size={18} />}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          <EditableField
            label="Loan Purpose"
            value={isEditing ? gettingStarted.loanPurpose : loanPurposeOptions.find(o => o.value === gettingStarted.loanPurpose)?.label}
            field="loanPurpose"
            isEditing={isEditing}
            onChange={(field, value) => handleFieldChange('gettingStarted', field, value)}
            options={loanPurposeOptions}
          />
          <EditableField
            label="Property Type"
            value={isEditing ? gettingStarted.propertyType : propertyTypeOptions.find(o => o.value === gettingStarted.propertyType)?.label}
            field="propertyType"
            isEditing={isEditing}
            onChange={(field, value) => handleFieldChange('gettingStarted', field, value)}
            options={propertyTypeOptions}
          />
          <EditableField
            label="Property Use"
            value={isEditing ? gettingStarted.propertyUse : propertyUseOptions.find(o => o.value === gettingStarted.propertyUse)?.label}
            field="propertyUse"
            isEditing={isEditing}
            onChange={(field, value) => handleFieldChange('gettingStarted', field, value)}
            options={propertyUseOptions}
          />
        </div>
      </Section>

      {/* Personal Information Section */}
      <Section title="Personal Information" icon={<User size={18} />}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          <EditableField
            label="First Name"
            value={personalInfo.firstName}
            field="firstName"
            isEditing={isEditing}
            onChange={(field, value) => handleFieldChange('personalInfo', field, value)}
          />
          <EditableField
            label="Middle Name"
            value={personalInfo.middleName}
            field="middleName"
            isEditing={isEditing}
            onChange={(field, value) => handleFieldChange('personalInfo', field, value)}
          />
          <EditableField
            label="Last Name"
            value={personalInfo.lastName}
            field="lastName"
            isEditing={isEditing}
            onChange={(field, value) => handleFieldChange('personalInfo', field, value)}
          />
          <EditableField
            label="Date of Birth"
            value={personalInfo.dateOfBirth}
            field="dateOfBirth"
            isEditing={isEditing}
            onChange={(field, value) => handleFieldChange('personalInfo', field, value)}
            type="date"
          />
          <EditableField
            label="SSN (Last 4)"
            value={formatSSN(personalInfo.ssnLast4 || personalInfo.ssn)}
            field="ssnLast4"
            isEditing={false} // SSN should not be editable
            onChange={() => {}}
          />
          <EditableField
            label="Citizenship Status"
            value={isEditing ? personalInfo.citizenship : citizenshipOptions.find(o => o.value === personalInfo.citizenship)?.label}
            field="citizenship"
            isEditing={isEditing}
            onChange={(field, value) => handleFieldChange('personalInfo', field, value)}
            options={citizenshipOptions}
          />
          <EditableField
            label="Marital Status"
            value={isEditing ? personalInfo.maritalStatus : maritalStatusOptions.find(o => o.value === personalInfo.maritalStatus)?.label}
            field="maritalStatus"
            isEditing={isEditing}
            onChange={(field, value) => handleFieldChange('personalInfo', field, value)}
            options={maritalStatusOptions}
          />
        </div>
      </Section>

      {/* Contact Information Section */}
      <Section title="Contact Information" icon={<MapPin size={18} />}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
          <EditableField
            label="Street Address"
            value={contactInfo.streetAddress}
            field="streetAddress"
            isEditing={isEditing}
            onChange={(field, value) => handleFieldChange('contactInfo', field, value)}
          />
          <EditableField
            label="Unit/Apt"
            value={contactInfo.unit}
            field="unit"
            isEditing={isEditing}
            onChange={(field, value) => handleFieldChange('contactInfo', field, value)}
          />
          <EditableField
            label="City"
            value={contactInfo.city}
            field="city"
            isEditing={isEditing}
            onChange={(field, value) => handleFieldChange('contactInfo', field, value)}
          />
          <EditableField
            label="State"
            value={isEditing ? contactInfo.state : stateOptions.find(o => o.value === contactInfo.state)?.label || contactInfo.state}
            field="state"
            isEditing={isEditing}
            onChange={(field, value) => handleFieldChange('contactInfo', field, value)}
            options={stateOptions}
          />
          <EditableField
            label="ZIP Code"
            value={contactInfo.zipCode}
            field="zipCode"
            isEditing={isEditing}
            onChange={(field, value) => handleFieldChange('contactInfo', field, value)}
          />
          <EditableField
            label="Phone"
            value={formatPhone(contactInfo.phone)}
            field="phone"
            isEditing={isEditing}
            onChange={(field, value) => handleFieldChange('contactInfo', field, value.replace(/\D/g, ''))}
            type="tel"
          />
          <EditableField
            label="Email"
            value={contactInfo.email}
            field="email"
            isEditing={isEditing}
            onChange={(field, value) => handleFieldChange('contactInfo', field, value)}
            type="email"
          />
        </div>
      </Section>

      {/* Employment Section */}
      <Section title="Employment" icon={<Briefcase size={18} />}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
          <EditableField
            label="Employment Status"
            value={isEditing ? employment.employmentStatus : employmentStatusOptions.find(o => o.value === employment.employmentStatus)?.label}
            field="employmentStatus"
            isEditing={isEditing}
            onChange={(field, value) => handleFieldChange('employment', field, value)}
            options={employmentStatusOptions}
          />
          <EditableField
            label="Employer Name"
            value={employment.employerName}
            field="employerName"
            isEditing={isEditing}
            onChange={(field, value) => handleFieldChange('employment', field, value)}
          />
          <EditableField
            label="Job Title"
            value={employment.jobTitle}
            field="jobTitle"
            isEditing={isEditing}
            onChange={(field, value) => handleFieldChange('employment', field, value)}
          />
          <EditableField
            label="Monthly Income"
            value={isEditing ? employment.monthlyIncome : formatCurrency(employment.monthlyIncome)}
            field="monthlyIncome"
            isEditing={isEditing}
            onChange={(field, value) => handleFieldChange('employment', field, value)}
          />
          <EditableField
            label="Start Date"
            value={employment.startDate}
            field="startDate"
            isEditing={isEditing}
            onChange={(field, value) => handleFieldChange('employment', field, value)}
            type="date"
          />
        </div>
      </Section>

      {/* Income Sources Section */}
      <Section title="Income Sources" icon={<DollarSign size={18} />}>
        {(income.incomes || []).length === 0 ? (
          <p style={{ margin: 0, fontStyle: 'italic', color: '#64748b' }}>No income sources listed</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {(income.incomes || []).map((inc, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(148, 163, 184, 0.1)' }}>
                <span style={{ color: '#94a3b8' }}>{inc.type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                <span style={{ color: '#e2e8f0', fontWeight: 500 }}>{formatCurrency(inc.amount)}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', marginTop: '8px', borderTop: '1px solid rgba(148, 163, 184, 0.2)' }}>
              <span style={{ color: '#e2e8f0', fontWeight: 600 }}>Total Monthly Income</span>
              <span style={{ color: '#22c55e', fontWeight: 700 }}>{formatCurrency(totalIncome)}</span>
            </div>
          </div>
        )}
      </Section>

      {/* Assets Section */}
      <Section title="Assets" icon={<Wallet size={18} />}>
        {(assets.assets || []).length === 0 ? (
          <p style={{ margin: 0, fontStyle: 'italic', color: '#64748b' }}>No assets listed</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {(assets.assets || []).map((asset, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(148, 163, 184, 0.1)' }}>
                <span style={{ color: '#94a3b8' }}>
                  {asset.type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  {asset.institution && ` - ${asset.institution}`}
                </span>
                <span style={{ color: '#e2e8f0', fontWeight: 500 }}>{formatCurrency(asset.balance)}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', marginTop: '8px', borderTop: '1px solid rgba(148, 163, 184, 0.2)' }}>
              <span style={{ color: '#e2e8f0', fontWeight: 600 }}>Total Assets</span>
              <span style={{ color: '#38bdf8', fontWeight: 700 }}>{formatCurrency(totalAssets)}</span>
            </div>
          </div>
        )}
      </Section>

      {/* Liabilities Section */}
      <Section title="Liabilities" icon={<CreditCard size={18} />}>
        {(liabilities.liabilities || []).length === 0 ? (
          <p style={{ margin: 0, fontStyle: 'italic', color: '#64748b' }}>No liabilities listed</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {(liabilities.liabilities || []).map((l, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid rgba(148, 163, 184, 0.1)' }}>
                <span style={{ color: '#94a3b8' }}>
                  {l.type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  {l.creditor && ` - ${l.creditor}`}
                  {l.willPayOff && ' (Will pay off)'}
                </span>
                <span style={{ color: '#e2e8f0', fontWeight: 500 }}>
                  {formatCurrency(l.balance)} ({formatCurrency(l.monthlyPayment)}/mo)
                </span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', marginTop: '8px', borderTop: '1px solid rgba(148, 163, 184, 0.2)' }}>
              <span style={{ color: '#e2e8f0', fontWeight: 600 }}>Total Liabilities</span>
              <span style={{ color: '#f87171', fontWeight: 700 }}>{formatCurrency(totalLiabilities)}</span>
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}

LoanApplicationTab.propTypes = {
  caseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  caseInfo: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    caseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    caseNumber: PropTypes.string,
    email: PropTypes.string,
    borrowerEmail: PropTypes.string,
    name: PropTypes.string,
  }),
  onUpdate: PropTypes.func,
};
